import { t } from "../../utils/I18n";
import { Modal } from "../../utils/Modal";

interface EquipmentSlot {
    label: string;
    status: "pending" | "approved" | "completed";
}

interface NoticeItem {
    id: number;
    title: string;
    excerpt: string;
    href: string;
}

interface StaffNoteItem {
    id: number;
    body: string;
    author: string;
    createdAt: string;
}

interface EquipmentData {
    name: string;
    sub: string;
    room: string;
    model: string;
    email: string;
    contact: string;
    phone: string;
    image: string;
    detailHref: string;
    applyHref: string;
    feeGuideHref: string;
    qaHref: string;
    slots: Record<string, EquipmentSlot[]>;
    staffNotes: string;
    notices: NoticeItem[];
    educations: NoticeItem[];
    notesHistoryUrl: string;
}

// 현재 선택된 장비 데이터 (모달 오픈 시 참조)
let currentEq: EquipmentData | null = null;

export const ReservationController = {
    index(): void {
        ReservationController.initSortDropdown();
        ReservationController.initEquipmentSelect();
        ReservationController.initStaffNotesExpand();
    },

    // ── Sort dropdown ──────────────────────────────────────────────────────────
    initSortDropdown(): void {
        const wrap = document.querySelector<HTMLElement>(".sec-rsv-layout__sort-wrap");
        if (!wrap) return;

        const btn  = wrap.querySelector<HTMLButtonElement>(".sec-rsv-layout__sort-btn");
        const menu = wrap.querySelector<HTMLElement>(".sec-rsv-layout__sort-menu");
        if (!btn || !menu) return;

        const open  = () => { menu.classList.add("is-open");    btn.setAttribute("aria-expanded", "true"); };
        const close = () => { menu.classList.remove("is-open"); btn.setAttribute("aria-expanded", "false"); };
        const toggle = () => menu.classList.contains("is-open") ? close() : open();

        btn.addEventListener("click", (e) => { e.stopPropagation(); toggle(); });
        document.addEventListener("click", (e) => { if (!wrap.contains(e.target as Node)) close(); });
        document.addEventListener("keydown", (e) => { if (e.key === "Escape") close(); });

        menu.querySelectorAll<HTMLElement>(".sec-rsv-layout__sort-option").forEach((opt) => {
            opt.addEventListener("click", () => {
                menu.querySelectorAll(".sec-rsv-layout__sort-option").forEach((o) => {
                    o.classList.remove("is-selected");
                    o.setAttribute("aria-selected", "false");
                });
                opt.classList.add("is-selected");
                opt.setAttribute("aria-selected", "true");

                const label = opt.textContent?.trim() || t("reservation.sort_by");
                const icon = btn.querySelector("i");
                btn.childNodes.forEach((n) => { if (n.nodeType === Node.TEXT_NODE) n.remove(); });
                btn.insertBefore(document.createTextNode(`${label} `), icon);
                close();
            });
        });
    },

    // ── Equipment select + API fetch ───────────────────────────────────────────
    initEquipmentSelect(): void {
        const items = document.querySelectorAll<HTMLAnchorElement>("[data-rsv-item]");
        if (!items.length) return;

        // 페이지 로드: is-active 항목 (또는 첫 번째) 자동 fetch
        const firstActive =
            (document.querySelector<HTMLAnchorElement>("[data-rsv-item].is-active") ?? items[0]) as HTMLAnchorElement;

        if (firstActive) {
            ReservationController.loadEquipment(firstActive);
        }

        items.forEach((item) => {
            item.addEventListener("click", (e) => {
                e.preventDefault();
                items.forEach((i) => i.classList.remove("is-active"));
                item.classList.add("is-active");
                ReservationController.loadEquipment(item);
            });
        });
    },

    // 우측 컬럼 로더 토글 — fetch 동안 오버레이 표시 (뚝뚝 끊김 완화)
    setLoading(on: boolean): void {
        const rightCol = document.querySelector<HTMLElement>("[data-rsv-right]");
        if (!rightCol) return;
        rightCol.classList.toggle("is-loading", on);
        const loader = rightCol.querySelector<HTMLElement>("[data-rsv-loader]");
        if (loader) loader.setAttribute("aria-hidden", on ? "false" : "true");
    },

    async loadEquipment(item: HTMLAnchorElement): Promise<void> {
        const url = item.dataset.rsvJsonUrl;
        if (!url) return;

        ReservationController.setLoading(true);
        try {
            let json: { success: boolean; data: EquipmentData } | null = null;
            try {
                const res = await fetch(url, { headers: { Accept: "application/json" } });
                if (!res.ok) return;
                json = (await res.json()) as { success: boolean; data: EquipmentData };
            } catch {
                return;
            }

            if (!json?.success || !json.data) return;

            const eq = json.data;
            currentEq = eq;
            ReservationController.updateDetailPanel(eq);
            ReservationController.updateSidebar(eq);
            ReservationController.updateNotices(eq);
            ReservationController.updateStaffNotes(eq);
            ReservationController.updateCalendar(eq);
        } finally {
            ReservationController.setLoading(false);
        }
    },

    // ── DOM update helpers ─────────────────────────────────────────────────────
    updateDetailPanel(eq: EquipmentData): void {
        const setText = (sel: string, val: string) => {
            const el = document.querySelector<HTMLElement>(sel);
            if (el) el.textContent = val;
        };

        // Image — 로드 실패(404) 시 placeholder 로 폴백
        const placeholder = document.querySelector<HTMLElement>("[data-rsv-img-placeholder]");
        const existingImg  = document.querySelector<HTMLImageElement>("[data-rsv-img]");

        const restorePlaceholder = (el: Element) => {
            const ph = document.createElement("span");
            ph.className = "ui-placeholder ui-placeholder--4x3";
            ph.setAttribute("aria-hidden", "true");
            ph.setAttribute("data-rsv-img-placeholder", "");
            el.replaceWith(ph);
        };

        if (eq.image) {
            if (existingImg) {
                existingImg.onerror = () => restorePlaceholder(existingImg);
                existingImg.src = eq.image;
                existingImg.alt = eq.name;
            } else if (placeholder) {
                const img = document.createElement("img");
                img.setAttribute("data-rsv-img", "");
                img.onerror = () => restorePlaceholder(img);
                img.src = eq.image;
                img.alt = eq.name;
                placeholder.replaceWith(img);
            }
        } else {
            if (existingImg) {
                restorePlaceholder(existingImg);
            }
        }

        setText("[data-rsv-name]",    eq.name);
        setText("[data-rsv-sub]",     eq.sub);
        setText("[data-rsv-room]",    eq.room);
        setText("[data-rsv-model]",   eq.model);
        setText("[data-rsv-email]",   eq.email);
        setText("[data-rsv-contact]", eq.contact);
        setText("[data-rsv-phone]",   eq.phone);

        // Details 버튼 → 모달 (페이지 이동 대신)
        const detailLink = document.querySelector<HTMLAnchorElement>("[data-rsv-detail-link]");
        if (detailLink) {
            detailLink.href = eq.detailHref || "#";
            detailLink.onclick = (e) => {
                e.preventDefault();
                if (currentEq) ReservationController.openDetailModal(currentEq);
            };
        }
    },

    updateSidebar(eq: EquipmentData): void {
        const applyLink = document.querySelector<HTMLAnchorElement>("[data-rsv-apply-link]");
        const feeLink   = document.querySelector<HTMLAnchorElement>("[data-rsv-fee-link]");
        const qaLink    = document.querySelector<HTMLAnchorElement>("[data-rsv-qa-link]");

        if (applyLink) applyLink.href = eq.applyHref    || "#";
        if (feeLink)   feeLink.href   = eq.feeGuideHref || "#";
        if (qaLink)    qaLink.href    = eq.qaHref        || "#";
    },

    updateNotices(eq: EquipmentData): void {
        const panel = document.querySelector<HTMLElement>(".rsv-notices");
        if (!panel) return;

        const notices    = eq.notices    ?? [];
        const educations = eq.educations ?? [];

        // 현재 활성 탭 파악
        const activeTabBtn = panel.querySelector<HTMLElement>(".rsv-notices__tab.is-active");
        const activeTab    = activeTabBtn?.dataset.rsvNoticesTab === "education" ? "education" : "notices";

        const renderList = (items: NoticeItem[], emptyMsg: string): string => {
            if (!items.length) {
                return `<li class="rsv-notices__empty" style="padding:1.6rem;text-align:center;color:var(--clr-text-sub);list-style:none">
                    <i class="fa-regular fa-inbox" aria-hidden="true" style="display:block;font-size:2rem;margin-bottom:.8rem"></i>
                    <span class="no-body-sm">${emptyMsg}</span>
                </li>`;
            }
            return items.map((item) =>
                `<li class="rsv-notices__item">
                    <a class="rsv-notices__tag no-body-xs" href="${item.href}">${item.title}</a>
                    <span class="rsv-notices__body no-body-sm">${item.excerpt}</span>
                </li>`
            ).join("");
        };

        const list = panel.querySelector<HTMLElement>(".rsv-notices__list");
        if (list) {
            list.innerHTML = activeTab === "education"
                ? renderList(educations, "No education items yet.")
                : renderList(notices,    "No notices yet.");
        }

        // 탭 클릭 핸들러 재등록
        panel.querySelectorAll<HTMLElement>("[data-rsv-notices-tab]").forEach((btn) => {
            btn.onclick = () => {
                panel.querySelectorAll("[data-rsv-notices-tab]").forEach((b) => {
                    b.classList.remove("is-active");
                    b.setAttribute("aria-selected", "false");
                });
                btn.classList.add("is-active");
                btn.setAttribute("aria-selected", "true");

                const tab   = btn.dataset.rsvNoticesTab === "education" ? "education" : "notices";
                const items = tab === "education" ? educations : notices;
                if (list) list.innerHTML = renderList(items, tab === "education" ? "No education items yet." : "No notices yet.");

                const count = panel.querySelector<HTMLElement>(".rsv-notices__pager-count");
                if (count) count.textContent = `${items.length} / ${items.length}`;
            };
        });

        const count = panel.querySelector<HTMLElement>(".rsv-notices__pager-count");
        const activeItems = activeTab === "education" ? educations : notices;
        if (count) count.textContent = `${activeItems.length} / ${activeItems.length}`;
    },

    updateStaffNotes(eq: EquipmentData): void {
        const body = document.querySelector<HTMLElement>("[data-rsv-staff-notes] .sec-rsv-layout__staff-notes-body");
        if (!body) return;
        // staffNotes 는 서버에서 nl2br(e(...)) 처리된 안전한 HTML
        body.innerHTML = eq.staffNotes || '<span class="no-body-sm" style="color:var(--clr-text-sub)">No staff notes available.</span>';
    },

    updateCalendar(eq: EquipmentData): void {
        const calWrap = document.querySelector<HTMLElement>("[data-rsv-cal-wrap]");
        if (!calWrap) return;

        const grid = calWrap.querySelector<HTMLElement>(".rsv-cal__grid");
        if (!grid) return;

        const slots = eq.slots || {};

        grid.querySelectorAll<HTMLElement>(".rsv-cal__week").forEach((row) => {
            row.querySelectorAll<HTMLElement>(".rsv-cal__cell:not(.rsv-cal__cell--empty)").forEach((cell) => {
                const dayEl = cell.querySelector<HTMLElement>(".rsv-cal__day-num");
                const day   = dayEl ? parseInt(dayEl.textContent?.trim() || "0", 10) : 0;
                if (!day) return;

                cell.querySelectorAll(".rsv-cal__slot").forEach((s) => s.remove());

                const daySlots: EquipmentSlot[] = (slots[String(day)] as EquipmentSlot[]) || [];
                cell.classList.toggle("rsv-cal__cell--has-slots", daySlots.length > 0);

                daySlots.forEach((slot) => {
                    const btn = document.createElement("button");
                    btn.type  = "button";
                    btn.className = `rsv-cal__slot rsv-cal__slot--${slot.status} no-body-xs`;
                    btn.title = slot.label;
                    btn.textContent = slot.label;
                    cell.appendChild(btn);
                });
            });
        });
    },

    // ── Details 모달 ───────────────────────────────────────────────────────────
    openDetailModal(eq: EquipmentData): void {
        const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

        const imgHtml = eq.image
            ? `<img src="${esc(eq.image)}" alt="${esc(eq.name)}" class="rsv-detail-modal__img">`
            : `<span class="ui-placeholder ui-placeholder--4x3 rsv-detail-modal__img" aria-hidden="true"></span>`;

        const metaRow = (label: string, val: string) =>
            val ? `<div class="rsv-detail-modal__row"><dt class="no-body-xs">${esc(label)}</dt><dd class="no-body-sm">${esc(val)}</dd></div>` : "";

        const noticeItems = (eq.notices ?? []).map((n) =>
            `<li><a href="${esc(n.href)}" class="no-body-sm">${esc(n.title)}</a></li>`
        ).join("") || `<li style="color:var(--clr-text-sub)" class="no-body-sm">No notices yet.</li>`;

        const educationItems = (eq.educations ?? []).map((n) =>
            `<li><a href="${esc(n.href)}" class="no-body-sm">${esc(n.title)}</a></li>`
        ).join("") || `<li style="color:var(--clr-text-sub)" class="no-body-sm">No education items yet.</li>`;

        const html = `
<div class="rsv-detail-modal">
    <div class="rsv-detail-modal__media">${imgHtml}</div>
    <div class="rsv-detail-modal__body">
        <p class="rsv-detail-modal__sub no-body-sm">${esc(eq.sub)}</p>
        <dl class="rsv-detail-modal__meta">
            ${metaRow("Room", eq.room)}
            ${metaRow("Model", eq.model)}
            ${metaRow("Email", eq.email)}
            ${metaRow("Contact", eq.contact)}
            ${metaRow("Phone", eq.phone)}
        </dl>
        <a href="${esc(eq.applyHref)}" class="ui-cta ui-cta--filled rsv-detail-modal__apply">
            Submit Application <i class="fa-regular fa-arrow-right" aria-hidden="true"></i>
        </a>
        <div class="rsv-detail-modal__lists">
            <div class="rsv-detail-modal__list-col">
                <p class="no-body-xs rsv-detail-modal__list-title">Notices</p>
                <ul>${noticeItems}</ul>
            </div>
            <div class="rsv-detail-modal__list-col">
                <p class="no-body-xs rsv-detail-modal__list-title">Education / Seminars</p>
                <ul>${educationItems}</ul>
            </div>
        </div>
    </div>
</div>`;

        Modal.content(html, eq.name, "wide").catch(() => {});
    },

    // ── Staff Notes "View Larger" 모달 ─────────────────────────────────────────
    initStaffNotesExpand(): void {
        const btn = document.querySelector<HTMLButtonElement>("[data-rsv-staff-notes] .sec-rsv-layout__staff-notes-expand");
        if (!btn) return;

        btn.addEventListener("click", () => {
            if (currentEq?.notesHistoryUrl) {
                ReservationController.openStaffNotesModal(currentEq.notesHistoryUrl, currentEq.name).catch(() => {});
            }
        });
    },

    async openStaffNotesModal(url: string, equipmentName: string): Promise<void> {
        let notes: StaffNoteItem[] = [];
        try {
            const res = await fetch(url, { headers: { Accept: "application/json" } });
            if (res.ok) {
                const json = (await res.json()) as { success: boolean; data: StaffNoteItem[] };
                if (json?.success && Array.isArray(json.data)) notes = json.data;
            }
        } catch { /* silent */ }

        const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

        const notesHtml = notes.length
            ? notes.map((note) => `
                <div class="rsv-notes-modal__item">
                    <div class="rsv-notes-modal__meta">
                        ${note.author ? `<span class="rsv-notes-modal__author no-body-xs">${esc(note.author)}</span>` : ""}
                        <time class="rsv-notes-modal__date no-body-xs" datetime="${esc(note.createdAt)}">${esc(note.createdAt)}</time>
                    </div>
                    <p class="rsv-notes-modal__body no-body-sm">${esc(note.body).replace(/\n/g, "<br>")}</p>
                </div>`).join("")
            : `<p style="color:var(--clr-text-sub);text-align:center;padding:2.4rem 0" class="no-body-sm">No staff notes recorded yet.</p>`;

        const html = `<div class="rsv-notes-modal">${notesHtml}</div>`;

        Modal.content(html, `${equipmentName} — Staff Notes`, "wide").catch(() => {});
    },
};
